/**
 * Vanilla-style chat overlay (bottom-left): recent lines fade after 10 s,
 * T opens the input, / opens it with the slash already typed, Enter sends,
 * Esc cancels. Messages word-wrap to the vanilla 320px chat width; Up/Down
 * recall sent lines. Holding Tab shows the player list. A status line (e.g.
 * "Host lost, reconnecting...") shows top-centre.
 *
 * Opening the input releases the pointer lock (vanilla shows a cursor in chat
 * too). It has to: with the lock held the browser eats Esc to drop the lock and
 * never delivers the key, so chat stayed open behind the pause menu.
 *
 * ONE overlay per page: single player and multiplayer share it, so use
 * `getChatOverlay()` (main.ts) instead of constructing a second one.
 */

import { mcText, textWidth } from './kit';

interface Line { el: HTMLDivElement; at: number }

const SHOW_MS = 10000;
const FADE_MS = 1000;
const MAX_LINES = 100;
/** Vanilla chat box: 320 GUI px wide, 10 lines closed, 20 with the input up. */
const CHAT_W = 320;
const LINES_CLOSED = 10;
const LINES_OPEN = 20;
const MAX_HISTORY = 100;

/** Word-wrap to `max` GUI px; a word wider than the box breaks mid-word. */
function wrapText(text: string, max: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    let w = word;
    const joined = line ? `${line} ${w}` : w;
    if (textWidth(joined) <= max) { line = joined; continue; }
    if (line) { out.push(line); line = ''; }
    while (textWidth(w) > max) {
      let n = 1;
      while (n < w.length && textWidth(w.slice(0, n + 1)) <= max) n++;
      out.push(w.slice(0, n));
      w = w.slice(n);
    }
    line = w;
  }
  out.push(line);
  return out;
}

let sharedOverlay: ChatOverlay | null = null;

/** The page's chat overlay, created on first use. */
export function getChatOverlay(parent: HTMLElement, scale: () => number): ChatOverlay {
  if (!sharedOverlay) sharedOverlay = new ChatOverlay(parent, scale);
  return sharedOverlay;
}

export class ChatOverlay {
  // assigned in the constructor unless a shared overlay already exists (then
  // `this` is thrown away and the existing instance is returned)
  private readonly root!: HTMLDivElement;
  private readonly log!: HTMLDivElement;
  private readonly input!: HTMLInputElement;
  private readonly list!: HTMLDivElement;
  private readonly status!: HTMLDivElement;
  private readonly lines: Line[] = [];
  /** Sent lines, oldest first; Up/Down walk them like vanilla. */
  private readonly history: string[] = [];
  private historyAt = 0;
  open = false;
  onSend: ((text: string) => void) | null = null;
  onClose: (() => void) | null = null;
  /** May the input open right now? main.ts sets it (pointer lock, no GUI).
   *  The lock is dropped while typing; onClose is where main.ts takes it back. */
  openGuard: (() => boolean) | null = null;

  constructor(parent: HTMLElement, private readonly scale: () => number) {
    // installNet() (src/net) still constructs its own overlay: hand back the
    // page's single instance instead of stacking a second chat on top of it.
    if (sharedOverlay) return sharedOverlay;
    sharedOverlay = this;
    this.root = document.createElement('div');
    this.root.style.cssText = 'position:fixed;left:0;bottom:0;z-index:35;pointer-events:none;';
    this.log = document.createElement('div');
    this.log.style.cssText = 'display:flex;flex-direction:column;align-items:flex-start;margin:0 0 4px 2px;';
    this.input = document.createElement('input');
    this.input.maxLength = 256;
    this.input.spellcheck = false;
    this.input.style.cssText =
      'display:none;pointer-events:auto;box-sizing:border-box;width:calc(100vw - 4px);margin:0 2px 2px 2px;' +
      'background:rgba(0,0,0,0.5);color:#fff;border:none;outline:none;font:16px monospace;padding:4px;';
    this.root.append(this.log, this.input);
    parent.appendChild(this.root);

    this.list = document.createElement('div');
    // z-index above the menus (40) but below the Multiplayer screen (46), which
    // draws its own status: "Hosting/Joined (N)" must be readable on the pause
    // menu and the title, where the pointer is not locked.
    this.list.style.cssText =
      'position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:45;display:none;' +
      'background:rgba(0,0,0,0.5);padding:4px 8px;pointer-events:none;';
    parent.appendChild(this.list);
    this.status = document.createElement('div');
    this.status.style.cssText = 'position:fixed;top:40px;left:50%;transform:translateX(-50%);z-index:45;display:none;pointer-events:none;';
    parent.appendChild(this.status);

    for (const ev of ['keydown', 'keyup', 'keypress'] as const) {
      this.input.addEventListener(ev, (e) => {
        // keydown must not reach the game (the player would walk while typing);
        // keyup/keypress deliberately do bubble, so a movement key held when
        // chat opened is released instead of staying stuck down.
        if (ev === 'keydown') e.stopPropagation();
        if (ev !== 'keydown') return;
        const k = e as KeyboardEvent;
        if (k.code === 'Enter' || k.code === 'NumpadEnter') {
          const t = this.input.value.trim();
          if (t) {
            if (this.history[this.history.length - 1] !== t) this.history.push(t);
            if (this.history.length > MAX_HISTORY) this.history.shift();
            this.onSend?.(t);
          }
          this.close();
        } else if (k.code === 'ArrowUp' || k.code === 'ArrowDown') {
          k.preventDefault();
          const at = this.historyAt + (k.code === 'ArrowUp' ? -1 : 1);
          if (at < 0 || at > this.history.length) return;
          this.historyAt = at;
          this.input.value = this.history[at] ?? '';
        }
      });
    }

    // T opens chat, / opens it ready for a command. Capturing on window puts
    // this ahead of the net layer's own T handler, so both modes behave alike
    // (and only ever one overlay opens).
    window.addEventListener('keydown', this.onGlobalKey, true);
    // While the input is up the mouse belongs to chat: no look, no mining, no
    // hotbar scroll (the pointer stays locked, as it does in vanilla's chat).
    for (const ev of ['mousemove', 'mousedown', 'mouseup', 'wheel'] as const) {
      window.addEventListener(ev, this.onGlobalMouse, true);
    }
  }

  private readonly onGlobalKey = (e: KeyboardEvent): void => {
    if (this.open) {
      // Chat owns the keyboard while it is up, wherever focus ended up: Esc
      // must close chat, never fall through to the game's Esc-is-pause branch.
      if (e.code === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.close();
      } else if (document.activeElement !== this.input) {
        e.stopImmediatePropagation();
        this.input.focus();
      }
      return;
    }
    if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
    const slash = e.code === 'Slash';
    if (e.code !== 'KeyT' && !slash) return;
    if (this.openGuard && !this.openGuard()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.show(slash ? '/' : '');
  };

  /** Swallow game mouse input while typing (clicks inside chat still land). */
  private readonly onGlobalMouse = (e: Event): void => {
    if (!this.open) return;
    if (e.target instanceof Node && this.root.contains(e.target)) return;
    e.stopImmediatePropagation();
    // the cursor is free while typing: a click elsewhere must not steal focus
    if (e.type === 'mousedown') e.preventDefault();
  };

  private textEl(text: string, color = '#ffffff'): HTMLCanvasElement {
    const s = this.scale();
    const cv = mcText(text, { color, shadow: true });
    cv.style.cssText = `width:${cv.width * s}px;height:${cv.height * s}px;image-rendering:pixelated;display:block;`;
    return cv;
  }

  add(name: string, text: string, system: boolean): void {
    // plain-text mirror of the canvas-drawn lines, so assistive / agent clients
    // (mcp/server.mjs) can read chat through window.__chatLog
    const w = window as unknown as { __chatLog?: Array<{ name: string; text: string; system: boolean; t: number }> };
    (w.__chatLog ??= []).push({ name, text, system, t: Date.now() });
    if (w.__chatLog.length > 200) w.__chatLog.shift();
    // every wrapped row is its own chat line, as in vanilla, on a fixed-width
    // strip (narrowed only when the window is too small for 320 GUI px)
    const s = this.scale();
    const width = Math.max(40, Math.min(CHAT_W, Math.floor((innerWidth - 8) / s)));
    const at = performance.now();
    for (const part of wrapText(system ? text : `<${name}> ${text}`, width)) {
      const row = document.createElement('div');
      row.style.cssText = `background:rgba(0,0,0,0.5);padding:1px 2px;box-sizing:content-box;width:${width * s}px;`;
      row.appendChild(this.textEl(part, system ? '#ffff55' : '#ffffff'));
      this.log.appendChild(row);
      this.lines.push({ el: row, at });
    }
    while (this.lines.length > MAX_LINES) this.lines.shift()!.el.remove();
    this.update();
  }

  show(prefix = ''): void {
    this.open = true;
    this.input.style.display = 'block';
    this.input.value = prefix;
    this.historyAt = this.history.length;
    if (document.pointerLockElement) document.exitPointerLock();
    setTimeout(() => this.input.focus(), 0); // don't type the T that opened it
    this.update();
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.input.blur();
    this.input.style.display = 'none';
    this.update();
    this.onClose?.();
  }

  setStatus(text: string | null): void {
    if (!text) { this.status.style.display = 'none'; return; }
    this.status.replaceChildren(this.textEl(text, '#ffff55'));
    this.status.style.display = 'block';
  }

  showPlayers(rows: Array<{ name: string; ping: number; isHost: boolean }> | null): void {
    if (!rows) { this.list.style.display = 'none'; return; }
    this.list.replaceChildren(...rows.map((r) => this.textEl(`${r.isHost ? '[Host] ' : ''}${r.name}   ${Math.round(r.ping)}ms`, r.isHost ? '#ffd700' : '#ffffff')));
    this.list.style.display = 'block';
  }

  /** Per frame: fade old lines (all visible while the input is open). */
  update(): void {
    const now = performance.now();
    const visible = this.lines.slice(this.open ? -LINES_OPEN : -LINES_CLOSED);
    for (const l of this.lines) {
      if (this.open) { l.el.style.display = visible.includes(l) ? '' : 'none'; l.el.style.opacity = '1'; continue; }
      const age = now - l.at;
      if (!visible.includes(l) || age > SHOW_MS + FADE_MS) { l.el.style.display = 'none'; continue; }
      l.el.style.display = '';
      l.el.style.opacity = age > SHOW_MS ? String(1 - (age - SHOW_MS) / FADE_MS) : '1';
    }
  }

  dispose(): void {
    if (sharedOverlay === this) sharedOverlay = null;
    window.removeEventListener('keydown', this.onGlobalKey, true);
    for (const ev of ['mousemove', 'mousedown', 'mouseup', 'wheel'] as const) {
      window.removeEventListener(ev, this.onGlobalMouse, true);
    }
    this.root.remove(); this.list.remove(); this.status.remove();
  }
}
